/**
 * One practice track as a page: Foundations at `/foundations`, and the Civil
 * and Power and Energy previews at `/civil` and `/power` (docs/foundations.md).
 *
 * Its own entry rather than a screen inside App, for the reason the project
 * board is (main.tsx): App boots a virtual machine, and nothing here needs
 * one. That is also what makes these the parts of the product that open at
 * once and work on a phone.
 *
 * Steps open in order. The record is this browser's own (lib/tracks.ts) and
 * is not a credential.
 */
import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Brand } from '../../ui';
import { Corner } from '../Corner';
import { track as record } from '../../lib/events';
import { sound } from '../../lib/sound';
import { ABOUT_PATH } from '../../lib/landing';
import { TRACK_STEPS, loadPassed, savePassed, type TrackId } from '../../lib/tracks';
import type { StepProps } from './engine';

export interface TrackText {
  docTitle: string;
  kicker: string;
  title: string;
  lede: string;
  honest: string;
  progress(done: number, total: number): string;
  finish: { title: string; lede: string };
}

export function TrackPage({
  track,
  text,
  steps,
  children,
}: {
  track: TrackId;
  text: TrackText;
  steps: Record<string, ComponentType<StepProps>>;
  /** What comes after the steps: the discipline picker, or the way back to it. */
  children: ReactNode;
}) {
  const ids = TRACK_STEPS[track];
  const [passed, setPassed] = useState<Set<string>>(() => loadPassed(track));

  useEffect(() => {
    document.title = text.docTitle;
  }, [text]);

  const pass = useCallback(
    (id: string) => {
      if (passed.has(id)) return;
      const next = new Set(passed);
      next.add(id);
      setPassed(next);
      savePassed(track, next);
      record('practice', { detail: `${track}:${id}` });
      if (next.size === ids.length) {
        record('practice', { detail: `${track}:done` });
        window.setTimeout(() => sound.clear(), 350);
      }
    },
    [passed, track, ids.length],
  );

  const done = passed.size;
  const finished = done === ids.length;
  const current = ids.find((id) => !passed.has(id)) ?? null;

  return (
    <>
      <Corner />
      <div className={`foundations foundations--${track}`}>
        <header className="foundations__bar">
          <Brand href={ABOUT_PATH} />
        </header>
        <main>
          <header className="foundations__head">
            <p className="foundations__kicker">{text.kicker}</p>
            <h1 className="foundations__title">{text.title}</h1>
            <p className="foundations__lede">{text.lede}</p>
            <p className="foundations__honest">{text.honest}</p>
            <div className="foundations__progress" role="group" aria-label={text.progress(done, ids.length)}>
              <span className="foundations__rail" aria-hidden="true">
                {ids.map((id) => (
                  <span key={id} className={passed.has(id) ? 'is-done' : id === current ? 'is-current' : ''} />
                ))}
              </span>
              <span className="foundations__count">{text.progress(done, ids.length)}</span>
            </div>
          </header>

          <ol className="foundations__steps">
            {ids.map((id, i) => {
              const Body = steps[id];
              if (!Body) return null;
              const state = passed.has(id) ? 'done' : id === current ? 'open' : 'locked';
              const nextHref = i < ids.length - 1 ? `#step-${ids[i + 1]}` : '#after';
              return (
                <li key={id} id={`step-${id}`} className={`fstep fstep--${state}`}>
                  <Body n={i + 1} state={state} nextHref={nextHref} onPass={() => pass(id)} />
                </li>
              );
            })}
          </ol>

          {finished && (
            <section className="foundations__finish" aria-labelledby="track-finish">
              <span className="foundations__confetti" aria-hidden="true">
                {Array.from({ length: 24 }, (_, i) => (
                  <span key={i} style={{ ['--x' as string]: `${(i * 37) % 100}%`, ['--t' as string]: `${(i % 6) * 90}ms` }} />
                ))}
              </span>
              <h2 id="track-finish">{text.finish.title}</h2>
              <p>{text.finish.lede}</p>
            </section>
          )}

          <div id="after">{children}</div>
        </main>
      </div>
    </>
  );
}
